import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Button, Modal, Input, InputNumber, Select, Segmented, Textarea, Spinner, message } from '../../ui';
import { request } from '../../api/client';
import { inr2 } from '../../lib/format';
import { FileUploadField } from '../../components/FileUploadField';
import { bankAcctOption } from '../billing/PaymentFormModal';
import { errorCode, errorText } from './SettlementApproveDialog';

const MODES = ['CASH', 'UPI', 'BANK_TRANSFER', 'CHEQUE', 'CARD', 'OTHER'];
const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * TOP UP THE REP'S WALLET, WITH A CLAIM IN VIEW.
 *
 * A claim settled from the wallet often leaves the rep with nothing to travel on. Giving them float again is NEW
 * company money — an advance, with its own payment mode, account and proof — and never part of the reimbursement:
 * paying what the company owes and handing over float are two separate payments.
 *
 *   Top up to  the wallet should hold this much afterwards (the server computes the difference under its lock)
 *   Add        add exactly this much
 */
export function WalletTopUpDialog({ reportId, onClose, onDone }: { reportId: string; onClose: () => void; onDone?: () => void }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<'TOP_UP_TO' | 'ADD'>('TOP_UP_TO');
  const [value, setValue] = useState<number | null>(null);
  const [mode, setMode] = useState<string | undefined>();
  const [bank, setBank] = useState<string | undefined>();
  const [reference, setReference] = useState('');
  const [proof, setProof] = useState<string | undefined>();
  const [remarks, setRemarks] = useState('');

  const { data: report, isLoading: reportLoading, isError: reportError } = useQuery({
    queryKey: ['expense-report', reportId],
    queryFn: () => request<any>({ url: `/expense-reports/${reportId}` }),
  });
  const repId = report?.repId ? String(report.repId) : null;
  const { data: wallet, isLoading: walletLoading, refetch: refetchWallet } = useQuery({
    queryKey: ['staff-wallet', repId],
    queryFn: () => request<any>({ url: `/wallets/${repId}` }),
    enabled: !!repId,
    refetchOnMount: 'always',
  });
  const { data: banks } = useQuery({
    queryKey: ['bank-account-options'],
    queryFn: () => request<any[]>({ url: '/company/bank-accounts/options' }),
  });

  const repName = report?.repName ?? 'the rep';
  const walletNow = Number(wallet?.currentBalance ?? 0);
  const entered = value != null && Number.isFinite(value) ? value : 0;
  const amount = kind === 'TOP_UP_TO' ? Math.max(0, round2(entered - walletNow)) : Math.max(0, round2(entered));
  const after = round2(walletNow + amount);

  const give = useConfirmedMutation(
    {
      title: `Give ${inr2(amount)} to ${repName}’s wallet?`,
      content: `This is new float, separate from any reimbursement. Their wallet goes from ${inr2(walletNow)} to ${inr2(after)}.`,
      okText: 'Yes, give it',
    },
    {
      mutationFn: () => request<any>({
        url: `/expense-reports/${reportId}/wallet-top-up`, method: 'POST',
        data: {
          mode: kind,
          ...(kind === 'TOP_UP_TO' ? { target: entered, expectedWallet: walletNow } : { amount: entered }),
          paymentMode: mode, ...(mode !== 'CASH' ? { bankAccountId: bank } : {}),
          ...(reference.trim() ? { paymentReference: reference.trim() } : {}),
          ...(proof ? { paymentProofUrl: proof } : {}),
          ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
        },
      }),
      onSuccess: (res: any) => {
        message.success(`${inr2(res?.amount ?? amount)} added to ${repName}’s wallet — it now holds ${inr2(res?.walletAfter ?? after)}`);
        qc.invalidateQueries({ queryKey: ['expense-report', reportId] });
        qc.invalidateQueries({ queryKey: ['staff-wallet', repId] });
        qc.invalidateQueries({ queryKey: ['wallets'] });
        qc.invalidateQueries({ queryKey: ['wallet-stats'] });
        onDone?.();
        onClose();
      },
      onError: (e: any) => {
        // The wallet moved while the dialog was open: show the new balance and let them check the figure again.
        if (errorCode(e) === 'MONEY_POSITION_CHANGED') { refetchWallet(); message.warning(errorText(e)); return; }
        message.error(errorText(e));
      },
    },
  );

  const missing = !(entered > 0) ? (kind === 'TOP_UP_TO' ? 'Enter what the wallet should hold afterwards.' : 'Enter the amount to add.')
    : amount <= 0 ? `The wallet already holds ${inr2(walletNow)} — there is nothing to top up.`
      : !mode ? 'Choose how the money was handed over.'
        : mode !== 'CASH' && !bank ? 'Choose the company account it left from.'
          : mode !== 'CASH' && !proof ? 'Attach the payment proof.' : null;
  const loading = reportLoading || (!!repId && walletLoading);

  return (
    <Modal
      open
      width={600}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={report ? `Top up wallet · ${repName}` : 'Top up wallet'}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={give.isPending} disabled={loading || !!missing} onClick={() => give.mutate()}>
            {amount > 0 ? `Give ${inr2(amount)}` : 'Give'}
          </Button>
        </>
      }
    >
      {loading || !report ? (
        reportError ? <div className="text-sm text-danger-600 dark:text-danger-500">Couldn’t load this claim.</div>
          : <div className="flex items-center justify-center gap-2 py-8 text-muted"><Spinner /> Loading…</div>
      ) : (
        <div className="space-y-4">
          <section className="rounded-card border border-line bg-surface px-3 py-2.5 text-sm">
            <div className="flex items-baseline justify-between gap-3 tabular-nums">
              <span className="text-muted">{repName}’s wallet now</span>
              <span className="type-metric-sm text-content">{inr2(walletNow)}</span>
            </div>
            <p className="mt-1 type-caption text-muted">
              New company float, given with {report.reportNo} in view. It is separate from the claim’s reimbursement and does not change it.
            </p>
          </section>

          <div>
            <label className="mb-1 block type-label text-content">How much <span className="text-danger-600">*</span></label>
            <div className="flex flex-wrap items-center gap-2">
              <Segmented
                options={[{ label: 'Top up to', value: 'TOP_UP_TO' }, { label: 'Add', value: 'ADD' }]}
                value={kind}
                onChange={(v) => setKind(v as 'TOP_UP_TO' | 'ADD')}
              />
              <InputNumber className="min-w-[160px] flex-1" min={0} prefix="₹" value={value}
                placeholder={kind === 'TOP_UP_TO' ? 'Wallet should hold' : 'Amount to add'}
                onChange={(v) => setValue(v == null ? null : Number(v))} />
            </div>
            <div className="mt-1.5 flex justify-between gap-3 rounded-control bg-surface-2 px-3 py-1.5 text-sm tabular-nums">
              <span className="text-muted">Gives <b className="text-content">{inr2(amount)}</b></span>
              <span className="text-muted">wallet {inr2(walletNow)} → <b className="text-brand-700 dark:text-brand-300">{inr2(after)}</b></span>
            </div>
          </div>

          <div>
            <label className="mb-1 block type-label text-content">Paid via <span className="text-danger-600">*</span></label>
            <Select className="w-full" placeholder="How was it handed over?" value={mode} onChange={setMode}
              options={MODES.map((m) => ({ value: m, label: m.replace(/_/g, ' ') }))} />
          </div>
          {mode && mode !== 'CASH' && (
            <div>
              <label className="mb-1 block type-label text-content">From account <span className="text-danger-600">*</span></label>
              <Select className="w-full" placeholder="Company bank account" value={bank} onChange={setBank}
                options={(banks ?? []).map(bankAcctOption)}
                notFoundContent="No active company bank account — add or reactivate one in Company & Billing setup" />
            </div>
          )}
          <div>
            <label className="mb-1 block type-label text-content">Reference <span className="text-subtle">(UTR / cheque / voucher no.)</span></label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. UTR 526114839027" />
          </div>
          <div>
            <label className="mb-1 block type-label text-content">
              Payment proof {mode && mode !== 'CASH' ? <span className="text-danger-600">*</span> : <span className="text-subtle">(optional for cash)</span>}
            </label>
            <FileUploadField category="receipt" value={proof} onChange={(url) => setProof(url)}
              buttonLabel={proof ? 'Replace proof' : 'Upload screenshot / slip / signed voucher'} />
          </div>
          <div>
            <label className="mb-1 block type-label text-content">Remarks <span className="text-subtle">(optional)</span></label>
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </div>
          {missing && <p className="type-caption text-subtle">{missing}</p>}
        </div>
      )}
    </Modal>
  );
}
